import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CaretDown,
  Copy,
  Gear,
  XCircle,
} from '@phosphor-icons/react';
import { TextAlignLeft } from '@phosphor-icons/react/dist/ssr';
import { memo, useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useEdges, useNodes, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../store/storeAuth';
import useFlowsStore from '../../store/storeFlows';
import { useNotificationStore } from '../../store/storeNotifications';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { isActiveEdge } from '../../util/findActiveHandle';
import { NodeProps } from '../../util/Types/NodeProps';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import Label from '../UI/Label/Label';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTable from '../UI/OutputTable/OutputTable';
import PreviewModal from '../UI/PreviewModal/PreviewModal';
import { useSessionStore } from '../../store/storeSessions';
import { nodeSchema } from '../../util/myFunctionsCoreDisplay';

type SmallMemorySessionType = {
  id: string;
  name: string;
  entries: {
    role: string;
    content: string;
    timestamp: string;
  }[];
}

function SmallMemory({ data, selected, id, type }: NodeProps) {
  const edges = useEdges();
  const nodes = useNodes();

  const [entriesAnchorEl, setEntriesAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const openEntriesPopover = Boolean(entriesAnchorEl);
  const popOverId = openEntriesPopover ? 'simple-popover' : undefined;

  const openSettingsPopover = Boolean(settingsAnchorEl);
  const popOverSettingsId = openSettingsPopover ? 'simple-popover' : undefined;

  const [whoIsChanging, setWhoIsChanging] = useState({
    value: '',
    name: '',
  });

  const [formData, setFormData] = useState({
    selectedType: 'Load All Entries',
    selectedNumber: 50,
    selectedIndex: 5,
    chat: false,
    clearOnExecution: false,
    selectedChat: 'default',
    sessionEnabled: false,
  });

  const { handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      ...formData,
      ...(data.selectedType && { selectedType: data.selectedType }),
      ...(data.selectedNumber && { selectedNumber: data.selectedNumber }),
      ...(data.selectedIndex && { selectedIndex: data.selectedIndex }),
      ...(data.chat && { chat: data.chat }),
      ...(data.clearOnExecution && { clearOnExecution: data.clearOnExecution }),
      ...(data.selectedChat && { selectedChat: data.selectedChat }),
      ...(data.sessionEnabled && { sessionEnabled: data.sessionEnabled }),
    },
  });

  const { setNodes: setNodeState } = useReactFlow();
  const [previewModal, setPreviewModal] = useState(false);

  const { setSchema, setSaving, flowId } = useFlowsStore(state => state);

  const setNotification = useNotificationStore(state => state.setNotification);

  const { activeSession, sessions, setActiveSession } = useSessionStore(state => state);

  const [isChangingDirectly, setIsChangingDirectly] = useState(false);

  useEffect(() => {
    setFormData({
      ...formData,
      chat: data.chat !== undefined ? data.chat : formData.chat,
      selectedType: data.selectedType || 'Load All Entries',
      selectedNumber: data.selectedNumber || 50,
      selectedIndex: data.selectedIndex || 5,
      clearOnExecution: data.clearOnExecution || false,
      selectedChat: data.selectedChat || 'default',
      sessionEnabled: data.sessionEnabled || false,
    });
  }, [
    data.chat,
    data.selectedType,
    data.selectedNumber,
    data.selectedIndex,
    data.clearOnExecution,
    data.selectedChat,
    data.sessionEnabled,
  ]);

  const outputsType = [
    { name: 'Pass All Entries', value: 'Load All Entries' },
    { name: 'Pass First X Entries', value: 'Load First X Entries' },
    { name: 'Pass First X Words', value: 'Load First X Words' },
    { name: 'Pass Last X Entries', value: 'Load Last X Entries' },
    { name: 'Pass Last X Words', value: 'Load Last X Words' },
  ];

  const isWords = watch('selectedType')?.includes('Words');

  const clearEntries = () => {
     const newEntries: any[] = [];
     const newUserSessionsChat = (data.userSessionsChat || []).map((chat: SmallMemorySessionType) => {
       if (chat.id === data.selectedChat) {
         return {
           ...chat,
           entries: [],
         };
       }
       return chat;
     });
 
     setNodeState((nds: any[]) => {
       const validNodes = nds.filter(n => n !== undefined && n !== null);
       const updatedNodes = validNodes.map((nd: any) => {
         if (nd.id === id) {
           return {
             ...nd,
             data: {
               ...nd.data,
               entries: newEntries,
               userSessionsChat: newUserSessionsChat,
             },
           };
         }
         return nd;
       });
 
       nodeSchema(updatedNodes, setSchema);
 
       return updatedNodes;
     });
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          changeType: 'changing-directly',
          objectData: {
            [whoIsChanging.name]: whoIsChanging.value,
          },
          setSchema,
        });

        setWhoIsChanging({
          value: '',
          name: '',
        });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  const onChangeOnExecute = (e: {
    target: { name: string; checked: boolean };
  }) => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: {
        clearOnExecution: e.target.checked,
      },
      setSchema,
    });
  };

  const onChangeAgentSession = (e: {
    target: { checked: boolean };
  }) => {
    if (formData.chat && !e.target.checked) {
      setNotification({
        message:
          'Agent Session cannot be disabled while Chat Mode is enabled. Please disable Chat Mode first.',
        type: 'error',
      });
      return;
    }

    const newSessionEnabledValue = e.target.checked;

    const objectDataToSave: any = {
      sessionEnabled: newSessionEnabledValue,
    };

    if (!newSessionEnabledValue) {
      objectDataToSave.selectedChat = 'default';
      objectDataToSave.entries = data.userSessionsChat?.find((s: SmallMemorySessionType) => s.id === 'default')?.entries || [];
      setFormData(prev => ({
        ...prev,
        sessionEnabled: newSessionEnabledValue,
        selectedChat: 'default',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        sessionEnabled: newSessionEnabledValue,
      }));
    }

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      flowId,
      setSaving,
      objectData: objectDataToSave,
      setSchema,
    });
  };

  const onChangeChatEnabled = (e: {
    target: { checked: boolean };
  }) => {
    const newChatValue = e.target.checked;

    if (newChatValue) {
      const otherSmallMemoryNodes = nodes.filter(
        (node: any) =>
          node.type === 'fluxObject' &&
          node.id !== id &&
          (node.data as any)?.chat === true,
      );

      if (otherSmallMemoryNodes.length > 0) {
        setNotification({
          message:
            'Only one Small Memory node can have chat enabled at a time. Please disable chat on other Small Memory nodes first.',
          type: 'error',
        });
        return;
      }

      const objectDataToSave: any = {
        chat: newChatValue,
      };

      if (!formData.sessionEnabled) {
        objectDataToSave.sessionEnabled = true;
        setFormData(prev => ({
          ...prev,
          chat: newChatValue,
          sessionEnabled: true,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          chat: newChatValue,
        }));
      }

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        objectData: objectDataToSave,
        setSchema,
      });
    } else {
      setFormData(prev => ({
        ...prev,
        chat: newChatValue,
      }));

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        flowId,
        setSaving,
        objectData: {
          chat: newChatValue,
        },
        setSchema,
      });
    }
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const { user } = useAuthStore(state => state);

  const onSubmit = (data: any) => {
    setFormData({
      ...data,
    });

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      changeType: 'execute',
      objectData: {
        ...data,
      },
      setSchema,
    });

    setEntriesAnchorEl(null);
  };

  useEffect(() => {
    if (!data.sessionEnabled || !activeSession?.id) return;

    const activeSessionData = data.userSessionsChat?.find(
      (s: SmallMemorySessionType) => s.id === activeSession.id
    );

    const entriesToSet = activeSessionData?.entries || [];

    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      changeType: 'initialize-session-contents',
      objectData: {
        entries: entriesToSet,
        selectedChat: activeSession.id,
      },
      setSchema,
    });
  }, []);

  useEffect(() => {
    function updateSessionContents() {
      if (!data.sessionEnabled) {
        return;
      }

      const currentContent = data.entries;
      if (data.selectedChat === activeSession.id) {
        return;
      }

      const previousSessionId = data.selectedChat;

      const smallMemorySessions = sessions.map(session => {
        const existingSession = data.userSessionsChat?.find((s: SmallMemorySessionType) => s.id === session.id);

        if (session.id === previousSessionId) {
          return {
            ...session,
            entries: currentContent,
          };
        }

        if (existingSession?.entries) {
          return {
            ...session,
            entries: existingSession.entries,
          }
        }

        return {
          ...session,
          entries: [],
        };
      });

      const activeSessionContent = smallMemorySessions.find(s => s.id === activeSession.id)?.entries || [];

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowId,
        changeType: 'update-session-contents',
        objectData: {
          entries: activeSessionContent,
          selectedChat: activeSession.id,
          userSessionsChat: smallMemorySessions,
        },
        setSchema,
      });
    }

    updateSessionContents();
  }, [data.sessionEnabled, activeSession.id, data.entries, data.clearOnExecution, data.userSessionsChat]);

  useEffect(() => {
    nodeDataAutoSaveDynamic({
      newEdges: edges,
      setNodeState,
      id,
      setSaving,
      flowId,
      changeType: 'execute',
      objectData: {
        entries: data.entries,
        userSessionsChat: data.userSessionsChat,
      },
      setSchema,
    });
  }, [data.entries, data.userSessionsChat]);

  return (
    <Node
      type={type}
      isLoading={false}
      id={id}
      data={data}
      showTokensUsed={false}
    >
      <Box p="16px">
        <Box position={'relative'}>
          <Box
            mb={'16px'}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Box display={'flex'} alignItems={'center'} gap={'12px'}>
              {formData.sessionEnabled &&
                data.userSessionsChat &&
                data.userSessionsChat.length > 1 ? (
                <Select
                  name="selectedChat"
                  value={activeSession.id}
                  onChange={e => setActiveSession(e.target.value)}
                  className="nodrag"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: '#FFF',
                    '& .MuiList-root': {
                      backgroundColor:
                        user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                    },
                    '& .MuiSvgIcon-root': {
                      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                    },
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: 'none',
                    '.MuiOutlinedInput-notchedOutline': { border: 0 },
                    bgcolor: user?.theme === 'dark' ? '#101828' : '#FFF',
                    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        '& .Mui-selected': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                        },
                        backgroundColor:
                          user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                      },
                    },
                  }}
                >
                  {data.userSessionsChat.map((chat: SmallMemorySessionType, i: number) => (
                    <MenuItem
                      key={chat.id}
                      value={chat.id}
                      sx={{
                        color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                      }}
                    >
                      {chat.name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Label sx={{ mb: '0' }} labelName="Stored Content" isAdvanced />
              )}
              {data.entries && data.entries?.length > 0 && (
                <Box display={'flex'} alignItems={'center'}>
                  <IconButton onClick={() => setPreviewModal(true)}>
                    <TextAlignLeft size={16} />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(
                        data.entries.map((e: any) => e.content).join('\n'),
                      );
                      setNotification({
                        message: 'Copied to clipboard',
                        type: 'success',
                      });
                    }}
                    title="Copy All Content"
                  >
                    <Copy size={16} />
                  </IconButton>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <IconButtonTooltip
                title={'Small Memory Settings'}
                onClick={event => setSettingsAnchorEl(event.currentTarget)}
              >
                <Gear
                  size={22}
                  color={openSettingsPopover ? '#3650DE' : '#98A2B3'}
                />
              </IconButtonTooltip>

              <Popover
                open={openSettingsPopover}
                anchorEl={settingsAnchorEl}
                id={popOverSettingsId}
                onClose={() => setSettingsAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Box
                  sx={{
                    width: '350px',
                    border: '1px solid var(--Neutral-300, #D0D5DD)',
                    padding: '16px',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '22px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Settings
                    </Typography>
                    <IconButton
                      sx={{ padding: '4px' }}
                      size="medium"
                      onClick={() => setSettingsAnchorEl(null)}
                    >
                      <XCircle color="#667085" />
                    </IconButton>
                  </Box>
                  <Box>
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Clear Execution
                    </Typography>
                    <Typography fontSize={'12px'} color={'#475467'}>
                      Clear the stored entries on every agent execution
                    </Typography>
                    <Switch
                      onChange={onChangeOnExecute}
                      checked={formData.clearOnExecution}
                    />
                  </Box>
                  <Box>
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Chat Mode
                    </Typography>
                    <Typography fontSize={'12px'} color={'#475467'}>
                      Enable or disable chat mode
                    </Typography>
                    <Switch
                      checked={formData.chat}
                      onChange={onChangeChatEnabled}
                    />
                  </Box>
                  <Box>
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Agent Session
                    </Typography>
                    <Typography fontSize={'12px'} color={'#475467'}>
                      Enable or disable agent session mode
                    </Typography>
                    <Switch
                      checked={formData.sessionEnabled}
                      onChange={onChangeAgentSession}
                      disabled={formData.chat}
                    />
                  </Box>
                </Box>
              </Popover>
              <Button
                onClick={event => setEntriesAnchorEl(event.currentTarget)}
                sx={{
                  backgroundColor: 'var(--Neutral-100, #F2F4F7)',
                  padding: '6px 10px',
                  borderRadius: '8px',
                  display: 'flex',
                  gap: '4px',
                  justifyContent: 'center',
                  color: 'var(--Neutral-800, #101828)',
                  fontSize: '14px',
                  fontWeight: '400',
                }}
              >
                <Typography>
                  {formData.selectedType
                    .replace(
                      'X',
                      isWords
                        ? String(formData.selectedNumber) || 'X'
                        : String(formData.selectedIndex) || 'X',
                    )
                    .replace('Load', 'Pass')}
                </Typography>
                <CaretDown weight="fill" />
              </Button>

              <Popover
                open={openEntriesPopover}
                anchorEl={entriesAnchorEl}
                id={popOverId}
                onClose={() => setEntriesAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <Box
                  sx={{
                    width: '350px',
                    border: '1px solid var(--Neutral-300, #D0D5DD)',
                    padding: '16px',
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '22px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Entries to Pass
                    </Typography>
                    <IconButton
                      sx={{ padding: '4px' }}
                      size="medium"
                      onClick={() => setEntriesAnchorEl(null)}
                    >
                      <XCircle color="#667085" />
                    </IconButton>
                  </Box>
                  <Box>
                    <Typography
                      fontSize="16px"
                      fontWeight={600}
                      lineHeight={'24px'}
                    >
                      Which Entries to Pass
                    </Typography>
                    <Typography fontSize={'12px'} color={'#475467'}>
                      Decide which of your stored entries to pass to other
                      nodes.
                    </Typography>
                  </Box>
                  <Box>
                    <Select
                      name="selectedType"
                      value={watch('selectedType')}
                      onChange={e => setValue('selectedType', e.target.value)}
                      className="nodrag"
                      size="small"
                      sx={{
                        '& .MuiList-root': {
                          backgroundColor:
                            user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                        },
                        '& .MuiSvgIcon-root': {
                          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        },
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: 'none',
                        '.MuiOutlinedInput-notchedOutline': { border: 0 },
                        bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
                        color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .Mui-selected': {
                              backgroundColor:
                                user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                            },
                            backgroundColor:
                              user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                          },
                        },
                      }}
                    >
                      {outputsType?.map(flow => (
                        <MenuItem
                          key={flow.value}
                          value={flow.value}
                          sx={{
                            color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
                          }}
                        >
                          {flow.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  {watch('selectedType') !== 'Load All Entries' && (
                    <Box className="w-100">
                      {isWords ? (
                        <TextField
                          className="w-50 nodrag"
                          name="selectedNumber"
                          type="number"
                          label="Number of Words to pass"
                          value={watch('selectedNumber')}
                          onChange={e =>
                            setValue('selectedNumber', Number(e.target.value))
                          }
                          size="small"
                          inputProps={{
                            min: 1,
                          }}
                          InputProps={{
                            style: {
                              height: '50px',
                              color:
                                user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            },
                          }}
                          sx={{
                            width: '100%',
                            height: '50px',
                            color:
                              user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            bgcolor:
                              user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                            border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                              }`,
                          }}
                        />
                      ) : (
                        <TextField
                          className="w-50 nodrag"
                          name="selectedIndex"
                          type="number"
                          label="Number of Entries to pass"
                          value={watch('selectedIndex')}
                          onChange={e =>
                            setValue('selectedIndex', Number(e.target.value))
                          }
                          inputProps={{
                            min: 1,
                          }}
                          InputProps={{
                            style: {
                              height: '50px',
                              color:
                                user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            },
                          }}
                          sx={{
                            width: '100%',
                            height: '50px',
                            color:
                              user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                            bgcolor:
                              user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                            border: `2px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                              }`,
                          }}
                        />
                      )}
                    </Box>
                  )}
                  <Button variant="contained" onClick={handleSubmit(onSubmit)}>
                    Save
                  </Button>
                </Box>
              </Popover>
            </Box>
          </Box>
          <OutputTable
            value={data.entries}
            entries={data.entries}
            showInputHandles={true}
            labelName={'Store Content'}
            findColor={findColor}
            activeHandle={isActiveEdge(edges, id, 'output', 'source')}
            inputActive={isActiveEdge(edges, id, 'inputAdd', 'target')}
            showRole={formData.sessionEnabled}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              gap: '8px',
            }}
          >
          </Box>
          <Box>
            <Button onClick={clearEntries}>Clear Entries</Button>
          </Box>
        </Box>
      </Box>

      <PreviewModal
        data={
          data.chat && data.userSessionsChat
            ? data.userSessionsChat.filter(
              (chat: SmallMemorySessionType) =>
                chat.id === (data.selectedChat || 'default'),
            )
            : []
        }
        isSmallMemory
        onHide={() => setPreviewModal(false)}
        show={previewModal}
      />
    </Node>
  );
}

export default memo(SmallMemory);
